import { getId, INavLinkGroup, MessageBar, MessageBarType, Nav, Stack } from '@fluentui/react'
import * as React from 'react'
import { ReactElement, useContext, useEffect, useState } from 'react'
import { MailFolder, Message } from '@microsoft/microsoft-graph-types'
import { normalizeAxiosError, NormalizedError } from '../../../reducers/errorReducer'
import { DefaultError } from '../../Diagnostics/Errors'
import { EmailItem } from './EmailList'
import { Destination, GraphError, IGraphArrayPayload } from '../../../context/AxiosContext'
import Loading from '../../../components/Loading'
import { stealthClick } from '../../StealthLink'
import useEmailFolders from '../../../hooks/useEmailFolders'
import useEntity from '../../../hooks/useEntity'
import useUser from '../../../hooks/useUser'
import useAxios from '../../../hooks/useAxios'
import { AxiosError } from 'axios'
import AuthorizationContext from '../../../context/AuthorizationContext'
import { DocumentOpenType } from '../../../context/GlobalContext'
import { useSettings } from '../../../hooks/useSettings'

type FolderNavLinkGroup = Omit<INavLinkGroup, 'groupData'> & { groupData: MailFolder }

export default function EmailLocationList({ item }: { item: EmailItem }): ReactElement {
    const axios = useAxios()
    const { settings } = useSettings()

    const { getBaseUrl } = useContext(AuthorizationContext)

    const [errors, setErrors] = useState<NormalizedError[]>([])
    const [messages] = useEntity<Message[], IGraphArrayPayload<Message>, GraphError>(
        internetMessageId => ({
            url: '/messages/',
            baseURL: Destination.Email,
            params: {
                $select: 'id,subject,parentFolderId,webLink',
                $filter: `internetMessageId eq '${internetMessageId}'`,
            },
            headers: { Prefer: 'IdType="ImmutableId"' },
        }),
        item.internetMessageId,
        result => result.data.value,
    )
    const [rootFolderId, folders] = useEmailFolders()
    const [groups, setGroups] = useState<INavLinkGroup[]>()
    const { currentEmail } = useUser()
    const [baseUrl, setBaseUrl] = useState<string>()

    useEffect(() => {
        if (currentEmail) {
            setBaseUrl(`${getBaseUrl(Destination.Hammock)}/email/${currentEmail}`)
        }
    }, [currentEmail])

    useEffect(() => {
        setErrors(
            [messages, rootFolderId, folders]
                .filter(response => response.rejected)
                .map(error => normalizeAxiosError(error.data as unknown as AxiosError)),
        )

        if (baseUrl && messages.loaded && rootFolderId.loaded && folders.loaded) {
            const mapFolder = (folder: MailFolder, messages?: Message[]): FolderNavLinkGroup => ({
                name: folder.displayName,
                // Add a copy of the folder, because it's supposed to be immutable.
                groupData: { ...folder },
                links: messages?.map(message => ({
                    key: message.id,
                    name: message.subject,
                    url: null,
                    onClick: () =>
                        stealthClick(
                            {
                                id: message.id,
                                universalId: message.internetMessageId,
                                destination: Destination.Email,
                                documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open,
                            },
                            axios,
                        ),
                })),
            })

            // Collect all mail folders that contain messages by first creating a list of unique parent folder IDs.
            const groupMap = [...new Set(messages.data.map(message => message.parentFolderId))]
                .map(parentFolderId => folders.data.get(parentFolderId))
                .reduce((map: Map<string, FolderNavLinkGroup>, parentFolder: MailFolder) => {
                    const childMessages = messages.data.filter(message => message.parentFolderId === parentFolder.id)
                    return map.set(parentFolder.id, mapFolder(parentFolder, childMessages))
                }, new Map<string, FolderNavLinkGroup>())

            // Traverse through all parent folders until we reach root, so we can construct a complete tree later on.
            do {
                // These IDs can be skipped, because they are either root or already in our folder list.
                const parentFolderIds = [rootFolderId.data, ...[...groupMap.keys()]]
                // Find all parent folders that we haven't added to our current folder list.
                const orphanFolderIds = [...groupMap.values()]
                    .filter(group => !parentFolderIds.includes(group.groupData.parentFolderId))
                    .map(group => group.groupData.parentFolderId)
                // There are no orphans left.
                if (orphanFolderIds.length === 0) {
                    break
                }
                orphanFolderIds.forEach(folderId => {
                    if (!parentFolderIds.includes(folderId)) {
                        groupMap.set(folderId, mapFolder(folders.data.get(folderId)))
                    }
                })
            } while (true)

            groupMap.forEach(group => {
                // Only set the groupData property if there exists a parent.
                if (group.groupData.parentFolderId !== rootFolderId.data) {
                    // The parent can be a group anywhere in the tree, even if their own parent is root.
                    const parentGroup = groupMap.get(group.groupData.parentFolderId)

                    // Use the object reference to set all children, because parentFolder is just a reference.
                    parentGroup.links = [
                        {
                            key: group.groupData.id,
                            name: group.name,
                            links: group.links || [],
                            isExpanded: true,
                            url: null,
                        },
                        ...(parentGroup.links || []),
                    ]
                }
            })

            setGroups([...groupMap.values()].filter(group => group.groupData.parentFolderId === rootFolderId.data))
        }
    }, [messages.data, rootFolderId.data, folders.data])

    if (errors?.length > 0) {
        return (
            <>
                {errors?.map(error => (
                    <DefaultError key={getId()} error={error} />
                ))}
            </>
        )
    }

    if (!groups) {
        return <Loading />
    }

    if (groups?.length === 0) {
        return (
            <Stack horizontalAlign="center" verticalAlign="center" tokens={{ padding: 'm' }}>
                <MessageBar messageBarType={MessageBarType.info}>Deze e-mail is niet gerepliceerd naar uw mailbox.</MessageBar>
            </Stack>
        )
    }

    return <Nav groups={groups} styles={{ groupContent: { marginBottom: 0 } }} />
}
